import React, { useState, useEffect } from 'react';
import { connectAuthPost } from './connectApi';
import ConnectSelect from './ConnectSelect';
import './styles/ConnectMeet.css';

/**
 * Create a meetup from a template.
 *
 * The spec asks for this to take under a minute, so a template fills in the
 * activity, duration, capacity and approval setting, leaving only a day and
 * a time to choose.
 *
 * No exact address is collected. The area field is a broad hint; anything
 * precise is meant to be shared in the meetup chat once people are approved.
 */
export default function CreateMeetup({ destinationId, activityTags, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    area: '',
    activity_tag_id: '',
    date: toInputDate(new Date(Date.now() + 3600000)),
    time: '18:00',
    duration: 120,
    capacity: 6,
    requires_approval: true,
    alcohol_free: false,
    invite_only: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await connectAuthPost('connect/meetups/templates');
        if (res.success) setTemplates(res.data.templates);
      } catch (err) { /* templates are optional */ }
    };
    load();
  }, []);

  const applyTemplate = (t) => {
    setTemplate(t.key);
    setForm((prev) => ({
      ...prev,
      title: t.title,
      activity_tag_id: t.activity_tag_id || '',
      duration: t.duration_minutes,
      capacity: t.capacity,
      requires_approval: !!t.requires_approval,
    }));
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.title.trim()) { setError('Give the meetup a title.'); return; }

    const start = new Date(`${form.date}T${form.time}`);
    if (isNaN(start.getTime())) { setError('Check the date and time.'); return; }

    const startAt = Math.floor(start.getTime() / 1000);
    const endAt = startAt + parseInt(form.duration, 10) * 60;

    setIsSaving(true);
    try {
      const res = await connectAuthPost('connect/meetups/create', {
        destination_id: destinationId,
        title: form.title.trim(),
        description: form.description.trim(),
        area: form.area.trim(),
        activity_tag_id: form.activity_tag_id ? parseInt(form.activity_tag_id, 10) : 0,
        template: template || '',
        start_at: startAt,
        end_at: endAt,
        capacity: parseInt(form.capacity, 10),
        visibility: form.invite_only ? 'invite_only' : 'destination',
        alcohol_free: form.alcohol_free,
        requires_approval: form.requires_approval,
      });

      if (res.success) onCreated();
      else setError(res.response || 'Could not create the meetup.');
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="meet-overlay" onClick={onClose}>
      <div className="meet-modal" onClick={(e) => e.stopPropagation()}>
        <div className="meet-modal-head">
          <h2>Create a meetup</h2>
          <button className="meet-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="meet-modal-body">
          {templates.length > 0 && (
            <>
              <label className="meet-label">Start from</label>
              <div className="meet-templates">
                {templates.map((t) => (
                  <button
                    key={t.key}
                    className={`meet-template ${template === t.key ? 'active' : ''}`}
                    onClick={() => applyTemplate(t)}
                    type="button"
                  >
                    <strong>{t.label}</strong>
                    <span>{t.best_for}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          <label className="meet-label">Title</label>
          <input
            className="meet-input"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Arrival coffee"
            maxLength={140}
          />

          <label className="meet-label">Activity</label>
          <ConnectSelect
            ariaLabel="Activity"
            value={form.activity_tag_id}
            placeholder="Choose an activity"
            options={activityTags.map((t) => ({ value: t.id, label: `${t.category} · ${t.name}` }))}
            onChange={(v) => update('activity_tag_id', v)}
          />

          <div className="meet-row">
            <div>
              <label className="meet-label">Date</label>
              <input type="date" className="meet-input" value={form.date}
                     onChange={(e) => update('date', e.target.value)} />
            </div>
            <div>
              <label className="meet-label">Start</label>
              <input type="time" className="meet-input" value={form.time}
                     onChange={(e) => update('time', e.target.value)} />
            </div>
            <div>
              <label className="meet-label">For</label>
              <ConnectSelect
                ariaLabel="How long"
                value={form.duration}
                options={[
                  { value: 60, label: '1 hour' },
                  { value: 90, label: '1.5 hours' },
                  { value: 120, label: '2 hours' },
                  { value: 180, label: '3 hours' },
                  { value: 240, label: '4 hours' },
                ]}
                onChange={(v) => update('duration', v)}
              />
            </div>
          </div>

          <label className="meet-label">Area <span className="meet-hint">broad only — share the exact spot in the chat</span></label>
          <input
            className="meet-input"
            value={form.area}
            onChange={(e) => update('area', e.target.value)}
            placeholder="Pietermaai"
            maxLength={120}
          />

          <label className="meet-label">Description</label>
          <textarea
            className="meet-input"
            rows={3}
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Who it is for, what to expect."
          />

          <div className="meet-row">
            <div>
              <label className="meet-label">Max people</label>
              <input type="number" min={2} max={50} className="meet-input" value={form.capacity}
                     onChange={(e) => update('capacity', e.target.value)} />
            </div>
          </div>

          <label className="meet-check">
            <input type="checkbox" checked={form.requires_approval}
                   onChange={(e) => update('requires_approval', e.target.checked)} />
            I approve who joins
          </label>
          <label className="meet-check">
            <input type="checkbox" checked={form.alcohol_free}
                   onChange={(e) => update('alcohol_free', e.target.checked)} />
            Alcohol-free
          </label>
          <label className="meet-check">
            <input type="checkbox" checked={form.invite_only}
                   onChange={(e) => update('invite_only', e.target.checked)} />
            Invite only — keep it out of browse
          </label>

          {error && <div className="meet-error">{error}</div>}
        </div>

        <div className="meet-modal-foot">
          <button className="meet-btn ghost" onClick={onClose}>Cancel</button>
          <button className="meet-btn primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Creating…' : 'Create meetup'}
          </button>
        </div>
      </div>
    </div>
  );
}

function toInputDate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
